import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useAxiosInstance from '../services/useAxiosInstance';
import { TbMessageChatbot, TbMessageChatbotFilled } from "react-icons/tb";

const ChatWidget = ({
    showCustomButton = true,
    ButtonText = TbMessageChatbot,
    buttonStyle = {},
    position = 'relative',
    customStyles = {}
}) => {
    const auth = useSelector((state) => state.auth);
    const [userData, setUserData] = useState({ firstName: '', lastName: '' });
    const [isLoading, setIsLoading] = useState(false);

    // Data fetching states
    const [userDataFetched, setUserDataFetched] = useState(false);
    const [amenityDataFetched, setAmenityDataFetched] = useState(false);
    const [serviceRequestDataFetched, setServiceRequestDataFetched] = useState(false);
    const [violationsDataFetched, setViolationsDataFetched] = useState(false);

    // Chat states
    const [chatLoaded, setChatLoaded] = useState(false);
    const [isVoiceflowInitialized, setIsVoiceflowInitialized] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);

    // Data states
    const [userBookings, setUserBookings] = useState([]);
    const [availableAmenities, setAvailableAmenities] = useState([]);
    const [amenityTypes, setAmenityTypes] = useState([]);
    const [userServiceRequests, setUserServiceRequests] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceRequestStatuses, setServiceRequestStatuses] = useState([]);
    const [userViolations, setUserViolations] = useState([]);
    const [violationTypes, setViolationTypes] = useState([]);
    const [violationStatuses, setViolationStatuses] = useState([]);

    // API operation states
    const [apiOperationInProgress, setApiOperationInProgress] = useState(false);
    const [lastApiOperation, setLastApiOperation] = useState(null);

    // Refs
    const abortControllerRef = useRef(null);
    const fetchInProgressRef = useRef(false);
    const lastFetchedUserIdRef = useRef(null);
    const refreshTimeoutRef = useRef(null);
    const voiceflowScriptRef = useRef(null); // Track the script element

    // Auth data
    const userId = auth.userId;
    const roleId = auth.roleId;
    const communityId = auth.communityId;
    const clientId = auth.clientId;
    const axiosInstance = useAxiosInstance();

    // Unique widget ID for this instance
    const widgetId = `chat-widget-${auth.userId || 'guest'}`;

    // Use useMemo for isDataReady to avoid initialization issues
    const isDataReady = useMemo(() => {
        return userDataFetched && amenityDataFetched && serviceRequestDataFetched && violationsDataFetched;
    }, [userDataFetched, amenityDataFetched, serviceRequestDataFetched, violationsDataFetched]);

    // Function to toggle chat open/close
    const toggleChat = useCallback(() => {
        if (window.voiceflow && window.voiceflow.chat) {
            if (isChatOpen) {
                window.voiceflow.chat.close();
                setIsChatOpen(false);
            } else {
                window.voiceflow.chat.open();
                setIsChatOpen(true);
            }
        }
    }, [isChatOpen]);

    // Helper function to calculate violation age
    const calculateViolationAge = useCallback((violationDate) => {
        const today = new Date();
        const vDate = new Date(violationDate);
        const diffTime = Math.abs(today - vDate);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }, []);

    // Helper function to dispatch custom events to Voiceflow
    const dispatchVoiceflowEvent = useCallback((type, payload) => {
        const event = new CustomEvent('message', {
            detail: JSON.stringify({ type, payload })
        });
        window.dispatchEvent(event);
        window.postMessage(JSON.stringify({ type, payload }), '*');
    }, []);

    // ===================================================================
    // VOICEFLOW CLEANUP FUNCTION
    // ===================================================================
    const cleanupVoiceflowChat = useCallback(() => {
        console.log('🧹 Cleaning up Voiceflow chat...');

        try {
            // Hide the chat widget if it exists
            if (window.voiceflow && window.voiceflow.chat && typeof window.voiceflow.chat.hide === 'function') {
                window.voiceflow.chat.hide();
                console.log('✅ Voiceflow chat hidden');
            }

            // Close the chat if it's open
            if (window.voiceflow && window.voiceflow.chat && typeof window.voiceflow.chat.close === 'function') {
                window.voiceflow.chat.close();
                console.log('✅ Voiceflow chat closed');
            }

            // Clean up global functions
            const globalFunctions = [
                'vhoasGetAmenityPricing', 'vhoasCheckAvailableSlots', 'vhoasCreateAmenityBooking',
                'vhoasCreateServiceRequest', 'vhoasGetAmenityTypes', 'vhoasGetServiceTypes',
                'vhoasGetAmenitiesByType', 'vhoasRefreshData', 'vhoasUpdateVoiceflow',
                'vhoasGetApiStatus', 'vhoasGetUserData'
            ];

            globalFunctions.forEach(func => {
                if (window[func]) {
                    delete window[func];
                    console.log(`✅ Cleaned up global function: ${func}`);
                }
            });

            // Remove the Voiceflow script if it exists
            if (voiceflowScriptRef.current && voiceflowScriptRef.current.parentNode) {
                voiceflowScriptRef.current.parentNode.removeChild(voiceflowScriptRef.current);
                voiceflowScriptRef.current = null;
                console.log('✅ Voiceflow script removed from DOM');
            }

            // Clean up window.voiceflow object
            if (window.voiceflow) {
                delete window.voiceflow;
                console.log('✅ Voiceflow object cleaned up');
            }

            // Reset component state
            setChatLoaded(false);
            setIsVoiceflowInitialized(false);
            setIsChatOpen(false);

            console.log('✅ Voiceflow cleanup completed');
        } catch (error) {
            console.error('❌ Error during Voiceflow cleanup:', error);
        }
    }, []);

    // ===================================================================
    // AUTHENTICATION CHANGE HANDLER
    // ===================================================================
    useEffect(() => {
        // When user logs out (auth.isAuthenticated becomes false)
        if (!auth.isAuthenticated && isVoiceflowInitialized) {
            console.log('🔐 User logged out, cleaning up Voiceflow chat...');
            cleanupVoiceflowChat();
        }
    }, [auth.isAuthenticated, isVoiceflowInitialized, cleanupVoiceflowChat]);

    // ===================================================================
    // API FUNCTIONS (Using useCallback to maintain stable references)
    // ===================================================================

    // 1. Get Amenity Pricing
    const getAmenityPricing = useCallback(async (amenityId) => {
        try {
            console.log(`🏊 Getting pricing for amenity ${amenityId}...`);
            const response = await axiosInstance.get(`/api/public/amenity/amount/${amenityId}`);
            console.log(`✅ Pricing fetched: $${response.data}`);
            return {
                success: true,
                data: response.data,
                message: `Pricing: $${response.data}`
            };
        } catch (error) {
            console.error('❌ Error fetching amenity pricing:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to get pricing information'
            };
        }
    }, [axiosInstance]);

    // 2. Check Available Slots
    const checkAvailableSlots = useCallback(async (amenityId, bookingDate) => {
        try {
            console.log(`🏊 Checking availability for amenity ${amenityId} on ${bookingDate}...`);
            const response = await axiosInstance.get(`/api/public/amenitybooking/availableSlots/${amenityId}`, {
                params: { bookingStart: bookingDate }
            });
            console.log(`✅ Found ${response.data.length} available slots`);
            return {
                success: true,
                data: response.data,
                message: `Found ${response.data.length} available time slots`
            };
        } catch (error) {
            console.error('❌ Error checking availability:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to check availability'
            };
        }
    }, [axiosInstance]);

    // 3. Create Amenity Booking
    const createAmenityBooking = useCallback(async (bookingData) => {
        try {
            console.log('🏊 Creating amenity booking...', bookingData);
            setApiOperationInProgress(true);

            if (!bookingData.amenityId || !bookingData.bookingStart || !bookingData.amenitySlots) {
                throw new Error('Missing required booking information');
            }

            const formattedData = {
                amenityBookingDTO: {
                    amenityBookingId: 0,
                    amenityTypeId: bookingData.amenityTypeId || 0,
                    amenityId: parseInt(bookingData.amenityId),
                    amenityBookingStatusId: 1,
                    amount: parseFloat(bookingData.amount) || 0,
                    bookedForClientId: parseInt(bookingData.bookedForClientId || clientId),
                    bookedUserId: parseInt(userId),
                    bookingDate: new Date().toISOString(),
                    bookingStart: new Date(bookingData.bookingStart).toISOString(),
                    bookingEnd: new Date(bookingData.bookingStart).toISOString(),
                    communityId: parseInt(communityId),
                    createdById: parseInt(userId),
                    createdDate: new Date().toISOString(),
                    modifiedById: null,
                    modifiedDate: new Date().toISOString(),
                    remarks: bookingData.remarks || "Booked via chat assistant"
                },
                amenitySlots: [parseInt(bookingData.amenitySlots)]
            };

            const response = await axiosInstance.post('/api/public/amenitybooking/add', formattedData);

            console.log('✅ Booking created successfully:', response.data);

            dispatchVoiceflowEvent('voiceflow:api_success', {
                operation: 'amenity_booking',
                details: response.data,
                bookingId: response.data.amenityBookingId
            });

            setTimeout(() => refreshUserData('amenity_booking'), 1000);

            setLastApiOperation({
                type: 'amenity_booking',
                timestamp: new Date().toISOString(),
                success: true,
                data: response.data
            });

            return {
                success: true,
                data: response.data,
                bookingId: response.data.amenityBookingId,
                message: `Booking confirmed! Confirmation #${response.data.amenityBookingId}`
            };

        } catch (error) {
            console.error('❌ Error creating booking:', error);

            dispatchVoiceflowEvent('voiceflow:api_error', {
                operation: 'amenity_booking',
                error: error.response?.data?.message || error.message
            });

            setLastApiOperation({
                type: 'amenity_booking',
                timestamp: new Date().toISOString(),
                success: false,
                error: error.message
            });

            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to create booking. Please try again.'
            };
        } finally {
            setApiOperationInProgress(false);
        }
    }, [axiosInstance, clientId, userId, communityId, dispatchVoiceflowEvent]);

    // 4. Create Service Request
    const createServiceRequest = useCallback(async (requestData) => {
        try {
            console.log('🔧 Creating service request...', requestData);
            setApiOperationInProgress(true);

            if (!requestData.serviceTypeId || !requestData.description || !requestData.requiredDate) {
                throw new Error('Missing required service request information');
            }

            const formattedData = {
                authenticationCode: "",
                communityId: parseInt(communityId),
                createdById: parseInt(userId),
                createdDate: new Date().toISOString(),
                description: requestData.description,
                invoiceNo: "",
                docUrl: requestData.docUrl || "",
                modifiedById: null,
                modifiedDate: new Date().toISOString(),
                requestedClientId: parseInt(requestData.requestedClientId || clientId),
                requiredDate: new Date(requestData.requiredDate).toISOString(),
                serviceCharge: 0,
                serviceRequestId: 0,
                serviceRequestStatusId: 58117,
                serviceTypeId: parseInt(requestData.serviceTypeId),
                vendorAccessCode: "",
                vendorId: null
            };

            const response = await axiosInstance.post('/api/public/servicerequest/add', formattedData);

            console.log('✅ Service request created successfully:', response.data);

            dispatchVoiceflowEvent('voiceflow:api_success', {
                operation: 'service_request',
                details: response.data,
                requestId: response.data.serviceRequestId,
                authCode: response.data.authenticationCode
            });

            setTimeout(() => refreshUserData('service_request'), 1000);

            setLastApiOperation({
                type: 'service_request',
                timestamp: new Date().toISOString(),
                success: true,
                data: response.data
            });

            return {
                success: true,
                data: response.data,
                requestId: response.data.serviceRequestId,
                authenticationCode: response.data.authenticationCode,
                message: `Service request submitted! Authentication code: ${response.data.authenticationCode}`
            };

        } catch (error) {
            console.error('❌ Error creating service request:', error);

            dispatchVoiceflowEvent('voiceflow:api_error', {
                operation: 'service_request',
                error: error.response?.data?.message || error.message
            });

            setLastApiOperation({
                type: 'service_request',
                timestamp: new Date().toISOString(),
                success: false,
                error: error.message
            });

            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to create service request. Please try again.'
            };
        } finally {
            setApiOperationInProgress(false);
        }
    }, [axiosInstance, communityId, userId, clientId, dispatchVoiceflowEvent]);

    // 5. Get Amenity Types
    const getAmenityTypes = useCallback(async () => {
        try {
            console.log('🏊 Getting amenity types...');
            const response = await axiosInstance.get(`/api/public/amenitytype/findByCommunityId/${communityId}`);
            const activeTypes = response.data.filter(type => type.activeStatus === true);
            console.log(`✅ Found ${activeTypes.length} amenity types`);
            return {
                success: true,
                data: activeTypes,
                message: `Found ${activeTypes.length} amenity types`
            };
        } catch (error) {
            console.error('❌ Error fetching amenity types:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to get amenity types'
            };
        }
    }, [axiosInstance, communityId]);

    // 6. Get Service Types
    const getServiceTypes = useCallback(async () => {
        try {
            console.log('🔧 Getting service types...');
            const response = await axiosInstance.get('/api/public/servicetype/getAll');
            const activeTypes = response.data.filter(type => type.activestatus === true);
            console.log(`✅ Found ${activeTypes.length} service types`);
            return {
                success: true,
                data: activeTypes,
                message: `Found ${activeTypes.length} service types`
            };
        } catch (error) {
            console.error('❌ Error fetching service types:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to get service types'
            };
        }
    }, [axiosInstance]);

    // 7. Get Amenities by Type
    const getAmenitiesByType = useCallback(async (amenityTypeId) => {
        try {
            console.log(`🏊 Getting amenities for type ${amenityTypeId}...`);
            const response = await axiosInstance.get(`/api/public/amenity/getByAmenityTypeId/${amenityTypeId}`);
            const activeAmenities = response.data.filter(
                amenity => amenity.activeStatus === true || amenity.activeStatus === "true"
            );
            console.log(`✅ Found ${activeAmenities.length} amenities`);
            return {
                success: true,
                data: activeAmenities,
                message: `Found ${activeAmenities.length} amenities`
            };
        } catch (error) {
            console.error('❌ Error fetching amenities:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message,
                message: 'Failed to get amenities'
            };
        }
    }, [axiosInstance]);

    // ===================================================================
    // DATA REFRESH FUNCTION
    // ===================================================================
    const refreshUserData = useCallback(async (operationType = 'general') => {
        if (!auth.isAuthenticated || !axiosInstance) {
            console.log('Cannot refresh data - not authenticated or no axios instance');
            return;
        }

        console.log(`🔄 Refreshing user data after ${operationType} operation...`);
        setApiOperationInProgress(true);

        try {
            let refreshPromises = [];

            if (operationType === 'amenity_booking' || operationType === 'general') {
                console.log('🏊 Refreshing amenity bookings...');
                const bookingsPromise = axiosInstance.get(`/api/public/amenitybooking/CommunityIdAndCreatedById`, {
                    params: {
                        bookedForClientId: roleId === 1003 ? clientId : 0,
                        communityId: auth.communityId,
                        endDate: '2026-12-31',
                        startDate: '2024-01-01',
                        amenityBookingStatusId: 0
                    }
                }).then(response => {
                    setUserBookings(response.data || []);
                    console.log(`✅ Amenity bookings refreshed: ${(response.data || []).length} bookings`);
                }).catch(error => {
                    console.error('❌ Error refreshing amenity bookings:', error);
                });
                refreshPromises.push(bookingsPromise);
            }

            if (operationType === 'service_request' || operationType === 'general') {
                console.log('🔧 Refreshing service requests...');
                let requestedClientId;
                if (roleId === 1000) {
                    requestedClientId = 0;
                } else if (roleId === 1001 || roleId === 1002) {
                    requestedClientId = 0;
                } else if (roleId === 1003) {
                    requestedClientId = clientId;
                } else {
                    requestedClientId = clientId;
                }

                const serviceRequestsPromise = axiosInstance.get(`/api/public/servicerequest/CommunityIdAndCreatedById`, {
                    params: {
                        requestedClientId: requestedClientId,
                        communityId: auth.communityId,
                        endDate: '2026-12-31',
                        startDate: '2024-01-01',
                        serviceRequestStatusId: 0
                    }
                }).then(response => {
                    setUserServiceRequests(response.data || []);
                    console.log(`✅ Service requests refreshed: ${(response.data || []).length} requests`);
                }).catch(error => {
                    console.error('❌ Error refreshing service requests:', error);
                });
                refreshPromises.push(serviceRequestsPromise);
            }

            await Promise.all(refreshPromises);
            console.log('✅ User data refresh completed successfully');

            if (window.voiceflow && window.voiceflow.chat && isChatOpen) {
                setTimeout(() => updateVoiceflowData(operationType), 500);
            }

            setLastApiOperation({
                type: operationType,
                timestamp: new Date().toISOString(),
                success: true
            });

        } catch (error) {
            console.error('❌ Error during data refresh:', error);
            setLastApiOperation({
                type: operationType,
                timestamp: new Date().toISOString(),
                success: false,
                error: error.message
            });
        } finally {
            setApiOperationInProgress(false);
        }
    }, [auth.isAuthenticated, auth.communityId, axiosInstance, roleId, clientId, isChatOpen]);

    // ===================================================================
    // UPDATE VOICEFLOW DATA FUNCTION
    // ===================================================================
    const updateVoiceflowData = useCallback((operationType = 'general') => {
        if (!window.voiceflow || !window.voiceflow.chat) {
            console.log('Voiceflow not available for data update');
            return;
        }

        try {
            console.log(`📡 Updating Voiceflow with fresh ${operationType} data...`);

            window.voiceflow.chat.interact({
                type: 'launch',
                payload: {
                    userId: String(auth.userId || ''),
                    communityId: String(auth.communityId || ''),
                    roleId: String(auth.roleId || ''),
                    clientId: String(auth.clientId || ''),
                    token: String(auth.token || ''),
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'User',
                    userBookings: userBookings,
                    availableAmenities: availableAmenities,
                    amenityTypes: amenityTypes,
                    userServiceRequests: userServiceRequests,
                    serviceTypes: serviceTypes,
                    serviceRequestStatuses: serviceRequestStatuses,
                    userViolations: userViolations,
                    violationTypes: violationTypes,
                    violationStatuses: violationStatuses,
                    apiIntegrationEnabled: true,
                    dataRefreshTimestamp: new Date().toISOString(),
                    lastOperation: operationType
                }
            });

            console.log('✅ Voiceflow data updated successfully');
        } catch (error) {
            console.error('❌ Error updating Voiceflow data:', error);
        }
    }, [auth, userData, userBookings, availableAmenities, amenityTypes, userServiceRequests, serviceTypes, serviceRequestStatuses, userViolations, violationTypes, violationStatuses]);

    // ===================================================================
    // EXPOSE GLOBAL FUNCTIONS WHEN DATA IS READY
    // ===================================================================
    useEffect(() => {
        if (auth.isAuthenticated && axiosInstance && isDataReady) {
            console.log('🌟 Exposing global API functions for Voiceflow...');

            // Main API functions
            window.vhoasGetAmenityPricing = getAmenityPricing;
            window.vhoasCheckAvailableSlots = checkAvailableSlots;
            window.vhoasCreateAmenityBooking = createAmenityBooking;
            window.vhoasCreateServiceRequest = createServiceRequest;
            window.vhoasGetAmenityTypes = getAmenityTypes;
            window.vhoasGetServiceTypes = getServiceTypes;
            window.vhoasGetAmenitiesByType = getAmenitiesByType;

            // Utility functions
            window.vhoasRefreshData = refreshUserData;
            window.vhoasUpdateVoiceflow = updateVoiceflowData;
            window.vhoasGetApiStatus = () => ({
                operationInProgress: apiOperationInProgress,
                lastOperation: lastApiOperation,
                dataReady: isDataReady,
                isAuthenticated: auth.isAuthenticated
            });

            window.vhoasGetUserData = () => ({
                userBookings,
                availableAmenities,
                amenityTypes,
                userServiceRequests,
                serviceTypes,
                serviceRequestStatuses,
                userViolations,
                violationTypes,
                violationStatuses
            });

            console.log('✅ Global API functions exposed successfully');
        } else {
            // Clean up global functions when not authenticated
            const globalFunctions = [
                'vhoasGetAmenityPricing', 'vhoasCheckAvailableSlots', 'vhoasCreateAmenityBooking',
                'vhoasCreateServiceRequest', 'vhoasGetAmenityTypes', 'vhoasGetServiceTypes',
                'vhoasGetAmenitiesByType', 'vhoasRefreshData', 'vhoasUpdateVoiceflow',
                'vhoasGetApiStatus', 'vhoasGetUserData'
            ];

            globalFunctions.forEach(func => {
                if (window[func]) delete window[func];
            });
        }

        return () => {
            // Cleanup global functions on unmount
            const globalFunctions = [
                'vhoasGetAmenityPricing', 'vhoasCheckAvailableSlots', 'vhoasCreateAmenityBooking',
                'vhoasCreateServiceRequest', 'vhoasGetAmenityTypes', 'vhoasGetServiceTypes',
                'vhoasGetAmenitiesByType', 'vhoasRefreshData', 'vhoasUpdateVoiceflow',
                'vhoasGetApiStatus', 'vhoasGetUserData'
            ];

            globalFunctions.forEach(func => {
                if (window[func]) delete window[func];
            });
        };
    }, [
        auth.isAuthenticated, axiosInstance, isDataReady, apiOperationInProgress, lastApiOperation,
        getAmenityPricing, checkAvailableSlots, createAmenityBooking, createServiceRequest,
        getAmenityTypes, getServiceTypes, getAmenitiesByType, refreshUserData, updateVoiceflowData,
        userBookings, availableAmenities, amenityTypes, userServiceRequests, serviceTypes,
        serviceRequestStatuses, userViolations, violationTypes, violationStatuses
    ]);

    // ===================================================================
    // DATA FETCHING useEffect HOOKS
    // ===================================================================

    // Reset state when user changes
    useEffect(() => {
        if (lastFetchedUserIdRef.current !== userId) {
            console.log('User changed, resetting state');
            setUserData({ firstName: '', lastName: '' });
            setUserDataFetched(false);
            setAmenityDataFetched(false);
            setServiceRequestDataFetched(false);
            setViolationsDataFetched(false);
            setChatLoaded(false);
            setIsVoiceflowInitialized(false);
            fetchInProgressRef.current = false;
            lastFetchedUserIdRef.current = userId;
            setApiOperationInProgress(false);
            setLastApiOperation(null);

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }

            // Clean up Voiceflow when user changes
            if (isVoiceflowInitialized) {
                cleanupVoiceflowChat();
            }
        }
    }, [userId, isVoiceflowInitialized, cleanupVoiceflowChat]);

    // Fetch user data
    useEffect(() => {
        const fetchUserData = async () => {
            if (!userId || !axiosInstance || fetchInProgressRef.current || userDataFetched) {
                return;
            }

            console.log('Fetching user data for userId:', userId);
            fetchInProgressRef.current = true;
            setIsLoading(true);

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            try {
                let response;
                const config = { signal: abortControllerRef.current.signal };

                if (roleId && communityId) {
                    try {
                        response = await axiosInstance.get(`/api/user/getById/${userId}`, {
                            ...config,
                            params: { roleId: roleId, communityId: communityId }
                        });
                    } catch (roleSpecificError) {
                        if (roleSpecificError.name === 'AbortError') throw roleSpecificError;
                        console.log('Role-specific fetch failed, falling back to basic user data');
                        response = await axiosInstance.get(`/api/user/getById/${userId}`, config);
                    }
                } else {
                    response = await axiosInstance.get(`/api/user/getById/${userId}`, config);
                }

                if (response?.data && !abortControllerRef.current.signal.aborted) {
                    const { firstName, lastName } = response.data;
                    setUserData({ firstName: firstName || '', lastName: lastName || '' });
                    console.log('User data fetched successfully:', { firstName, lastName });
                    setUserDataFetched(true);
                }

            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('User data fetch aborted');
                    return;
                }
                console.error('Error fetching user data:', error);
                setUserData({ firstName: '', lastName: '' });
                setUserDataFetched(true);
            } finally {
                setIsLoading(false);
                fetchInProgressRef.current = false;
            }
        };

        if (userId && axiosInstance && !userDataFetched && !fetchInProgressRef.current) {
            fetchUserData();
        }
    }, [userId, axiosInstance, roleId, communityId, userDataFetched]);

    // Fetch amenity data
    useEffect(() => {
        const fetchAmenityData = async () => {
            if (!auth.isAuthenticated || !auth.communityId || !auth.clientId || !axiosInstance || amenityDataFetched) {
                return;
            }

            console.log('Fetching amenity data...');
            setIsLoading(true);

            try {
                const bookingsResponse = await axiosInstance.get(`/api/public/amenitybooking/CommunityIdAndCreatedById`, {
                    params: {
                        bookedForClientId: auth.clientId,
                        communityId: auth.communityId,
                        endDate: '2026-12-31',
                        startDate: '2024-01-01',
                        amenityBookingStatusId: 0
                    }
                });
                setUserBookings(bookingsResponse.data || []);

                const amenityTypesResponse = await axiosInstance.get(`/api/public/amenitytype/findByCommunityId/${auth.communityId}`);
                if (amenityTypesResponse.data && Array.isArray(amenityTypesResponse.data)) {
                    const activeTypes = amenityTypesResponse.data.filter(type => type.activeStatus === true);
                    setAmenityTypes(activeTypes);
                }

                const amenitiesResponse = await axiosInstance.get(`/api/public/amenity/findByCommunityId/${auth.communityId}`);
                if (amenitiesResponse.data && Array.isArray(amenitiesResponse.data)) {
                    const activeAmenities = amenitiesResponse.data.filter(amenity =>
                        amenity.activeStatus === true || amenity.activeStatus === "true"
                    );
                    setAvailableAmenities(activeAmenities);
                }

                setAmenityDataFetched(true);
                console.log('✅ Amenity data fetched successfully');
            } catch (error) {
                console.error('Error fetching amenity data:', error);
                setUserBookings([]);
                setAvailableAmenities([]);
                setAmenityTypes([]);
                setAmenityDataFetched(true);
            } finally {
                setIsLoading(false);
            }
        };

        if (auth.isAuthenticated && auth.communityId && auth.clientId && axiosInstance && !amenityDataFetched) {
            fetchAmenityData();
        }
    }, [auth.isAuthenticated, auth.communityId, auth.clientId, axiosInstance, amenityDataFetched]);

    // Fetch service request data
    useEffect(() => {
        const fetchServiceRequestData = async () => {
            if (!auth.isAuthenticated || !auth.communityId || !axiosInstance || serviceRequestDataFetched) {
                return;
            }

            console.log('Fetching service request data...');
            setIsLoading(true);

            try {
                let requestedClientId;
                if (roleId === 1000) {
                    requestedClientId = 0;
                } else if (roleId === 1001 || roleId === 1002) {
                    requestedClientId = 0;
                } else {
                    requestedClientId = clientId;
                }

                const serviceRequestsResponse = await axiosInstance.get(`/api/public/servicerequest/CommunityIdAndCreatedById`, {
                    params: {
                        requestedClientId: requestedClientId,
                        communityId: auth.communityId,
                        endDate: '2026-12-31',
                        startDate: '2024-01-01',
                        serviceRequestStatusId: 0
                    }
                });
                setUserServiceRequests(serviceRequestsResponse.data || []);

                const serviceTypesResponse = await axiosInstance.get(`/api/public/servicetype/getAll`);
                if (serviceTypesResponse.data && Array.isArray(serviceTypesResponse.data)) {
                    const activeTypes = serviceTypesResponse.data.filter(type => type.activestatus === true);
                    setServiceTypes(activeTypes);
                }

                const statusesResponse = await axiosInstance.get(`/api/serviceRequestStatus/getAll`);
                if (statusesResponse.data && Array.isArray(statusesResponse.data)) {
                    setServiceRequestStatuses(statusesResponse.data);
                }

                setServiceRequestDataFetched(true);
                console.log('✅ Service request data fetched successfully');
            } catch (error) {
                console.error('Error fetching service request data:', error);
                setUserServiceRequests([]);
                setServiceTypes([]);
                setServiceRequestStatuses([]);
                setServiceRequestDataFetched(true);
            } finally {
                setIsLoading(false);
            }
        };

        if (auth.isAuthenticated && auth.communityId && axiosInstance && !serviceRequestDataFetched) {
            fetchServiceRequestData();
        }
    }, [auth.isAuthenticated, auth.communityId, roleId, clientId, axiosInstance, serviceRequestDataFetched]);

    // Fetch violations data
    useEffect(() => {
        const fetchViolationsData = async () => {
            if (!auth.isAuthenticated || !auth.communityId || !axiosInstance || violationsDataFetched) {
                return;
            }

            console.log('Fetching violations data...');
            setIsLoading(true);

            try {
                let clientIdget;
                if (roleId === 1000) {
                    clientIdget = 0;
                } else if (roleId === 1001 || roleId === 1002) {
                    clientIdget = 0;
                } else {
                    clientIdget = clientId;
                }

                const violationsResponse = await axiosInstance.get(`/api/violation/CommunityIdAndCreatedById`, {
                    params: {
                        communityId: auth.communityId,
                        clientId: clientIdget,
                        endDate: '2026-12-31',
                        startDate: '2024-01-01',
                        violationStatusId: 0
                    }
                });

                const enhancedViolations = (violationsResponse.data || []).map(violation => ({
                    ...violation,
                    violationAge: calculateViolationAge(violation.violationDate),
                    isOverdue: violation.violationStatusId === 2 && calculateViolationAge(violation.violationDate) > 30,
                    violationSeverity: violation.amount > 100 ? 'High' : violation.amount > 50 ? 'Medium' : 'Low',
                    formattedAmount: `$${violation.amount || 0}`,
                    formattedDate: new Date(violation.violationDate).toLocaleDateString()
                }));
                setUserViolations(enhancedViolations);

                const violationTypesResponse = await axiosInstance.get(`/api/violationtype/findByCommunityId/${auth.communityId}`);
                if (violationTypesResponse.data && Array.isArray(violationTypesResponse.data)) {
                    const enhancedTypes = violationTypesResponse.data
                        .filter(type => type.activeStatus === true)
                        .map(type => ({
                            ...type,
                            formattedAmount: `$${type.amount || 0}`,
                            severity: type.amount > 100 ? 'High' : type.amount > 50 ? 'Medium' : 'Low'
                        }));
                    setViolationTypes(enhancedTypes);
                }

                const violationStatusesResponse = await axiosInstance.get(`/api/violationStatus/getAll`);
                if (violationStatusesResponse.data && Array.isArray(violationStatusesResponse.data)) {
                    setViolationStatuses(violationStatusesResponse.data);
                }

                setViolationsDataFetched(true);
                console.log('✅ Violations data fetched successfully');
            } catch (error) {
                console.error('Error fetching violations data:', error);
                setUserViolations([]);
                setViolationTypes([]);
                setViolationStatuses([]);
                setViolationsDataFetched(true);
            } finally {
                setIsLoading(false);
            }
        };

        if (auth.isAuthenticated && auth.communityId && axiosInstance && !violationsDataFetched) {
            fetchViolationsData();
        }
    }, [auth.isAuthenticated, auth.communityId, roleId, clientId, axiosInstance, violationsDataFetched, calculateViolationAge]);

    // ===================================================================
    // VOICEFLOW EVENT HANDLER
    // ===================================================================
    useEffect(() => {
        const handleVoiceflowApiEvents = (event) => {
            if (event.data && typeof event.data === 'string') {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'voiceflow:api_success') {
                        const { operation, details } = data.payload || {};
                        console.log(`🎉 Voiceflow API Success: ${operation}`, details);

                        if (refreshTimeoutRef.current) {
                            clearTimeout(refreshTimeoutRef.current);
                        }

                        refreshTimeoutRef.current = setTimeout(() => {
                            if (operation === 'amenity_booking') {
                                refreshUserData('amenity_booking');
                            } else if (operation === 'service_request') {
                                refreshUserData('service_request');
                            } else {
                                refreshUserData('general');
                            }
                        }, 1000);
                    }

                    if (data.type === 'voiceflow:api_error') {
                        const { operation, error } = data.payload || {};
                        console.error(`❌ Voiceflow API Error: ${operation}`, error);
                        setLastApiOperation({
                            type: operation,
                            timestamp: new Date().toISOString(),
                            success: false,
                            error: error
                        });
                    }

                    if (data.type === 'voiceflow:open') {
                        setIsChatOpen(true);
                    } else if (data.type === 'voiceflow:close') {
                        setIsChatOpen(false);
                    }
                } catch (e) {
                    // Not a JSON message, ignore
                }
            }
        };

        window.addEventListener('message', handleVoiceflowApiEvents);
        console.log('🎧 Enhanced Voiceflow API event listeners set up');

        return () => {
            window.removeEventListener('message', handleVoiceflowApiEvents);
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [refreshUserData]);

    // ===================================================================
    // INITIALIZE VOICEFLOW WHEN DATA IS READY
    // ===================================================================
    useEffect(() => {
        if (isDataReady && !isVoiceflowInitialized && auth.isAuthenticated) {
            console.log('📋 Data is ready, initializing Voiceflow chat...');
            initializeVoiceflowChat();
        }
    }, [isDataReady, isVoiceflowInitialized, auth.isAuthenticated]);

    const initializeVoiceflowChat = useCallback(async () => {
        if (!auth.isAuthenticated || !auth.userId || !auth.communityId || !auth.token || !isDataReady) {
            console.error('Missing required data for chat loading');
            return;
        }

        console.log('=== Initializing Enhanced Voiceflow Chat with FULL API Integration ===');

        const fullName = (() => {
            const userDataName = `${userData.firstName} ${userData.lastName}`.trim();
            if (userDataName && userDataName !== ' ') return userDataName;

            if (auth.emailId) {
                const emailPrefix = auth.emailId.split('@')[0];
                const cleanName = emailPrefix.replace(/[^a-zA-Z]/g, '');
                return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
            }

            return 'User';
        })();

        const userIdValue = String(auth.userId || '');
        const firstNameValue = userData.firstName || 'User';
        const lastNameValue = userData.lastName || '';
        const clientIdValue = String(auth.clientId || '');

        console.log('=== Enhanced Voiceflow Variables ===');
        console.log('userId:', userIdValue);
        console.log('name:', fullName);
        console.log('🚀 FULL API Integration: ENABLED');

        (function (d, t) {
            var v = d.createElement(t), s = d.getElementsByTagName(t)[0];

            v.onload = function () {
                if (window.voiceflow && window.voiceflow.chat) {
                    console.log('Voiceflow script loaded, initializing FULL API-integrated chat...');

                    try {
                        window.voiceflow.chat.load({
                            verify: { projectID: '68385f4f5620d6bb614cbe71' },
                            url: 'https://general-runtime.voiceflow.com',
                            versionID: 'production',
                            userID: userIdValue,
                            user: {
                                name: fullName,
                                userID: userIdValue
                            },
                            launch: {
                                event: {
                                    type: "launch",
                                    payload: {
                                        userId: userIdValue,
                                        user_id: userIdValue,
                                        communityId: String(auth.communityId || ''),
                                        roleId: String(auth.roleId || ''),
                                        emailId: String(auth.emailId || ''),
                                        token: String(auth.token || ''),
                                        name: String(fullName || ''),
                                        firstName: String(firstNameValue || ''),
                                        lastName: String(lastNameValue || ''),
                                        clientId: String(clientIdValue || ''),
                                        userBookings: userBookings,
                                        availableAmenities: availableAmenities,
                                        amenityTypes: amenityTypes,
                                        userServiceRequests: userServiceRequests,
                                        serviceTypes: serviceTypes,
                                        serviceRequestStatuses: serviceRequestStatuses,
                                        userViolations: userViolations,
                                        violationTypes: violationTypes,
                                        violationStatuses: violationStatuses,
                                        apiIntegrationEnabled: true,
                                        canCreateBookings: true,
                                        canCreateServiceRequests: true,
                                        canCheckAvailability: true,
                                        canGetPricing: true,
                                        canGetAmenityTypes: true,
                                        canGetServiceTypes: true,
                                        canGetAmenitiesByType: true,
                                        supportsDataRefresh: true,
                                        lastDataFetch: new Date().toISOString(),
                                        apiOperationInProgress: apiOperationInProgress,
                                        lastApiOperation: lastApiOperation,
                                        globalFunctionsReady: true
                                    }
                                }
                            },
                            render: {
                                mode: 'overlay',
                                launcher: 'hidden' // Hide the default launcher since we have custom button
                            },
                            styling: { primary: '#3B82F6' }
                        }).then(() => {
                            console.log('✅ Enhanced Voiceflow chat with FULL API integration loaded successfully');
                            setChatLoaded(true);
                            setIsVoiceflowInitialized(true);

                            console.log('🚀 Chat initialized with FULL API integration:');
                            console.log('  ✅ Amenity booking creation');
                            console.log('  ✅ Service request submission');
                            console.log('  ✅ Real-time availability checking');
                            console.log('  ✅ Dynamic pricing retrieval');
                            console.log('  ✅ Data type fetching');
                            console.log('  ✅ Bidirectional data sync');

                        }).catch((loadError) => {
                            console.error('❌ Failed to load enhanced Voiceflow chat:', loadError);
                            setIsVoiceflowInitialized(false);
                            setChatLoaded(false);
                        });

                    } catch (error) {
                        console.error('❌ Error loading enhanced Voiceflow chat:', error);
                        setIsVoiceflowInitialized(false);
                        setChatLoaded(false);
                    }
                }
            };

            v.onerror = function (error) {
                console.error('❌ Failed to load Voiceflow chat widget:', error);
            };

            const existingScript = document.querySelector('script[src="https://cdn.voiceflow.com/widget-next/bundle.mjs"]');
            if (existingScript) {
                console.log('Voiceflow script already exists, triggering onload');
                setTimeout(() => v.onload(), 100);
                return;
            }

            v.src = "https://cdn.voiceflow.com/widget-next/bundle.mjs";
            v.type = "text/javascript";
            s.parentNode.insertBefore(v, s);

            // Store reference to the script element
            voiceflowScriptRef.current = v;
        })(document, 'script');
    }, [auth, userData, userBookings, availableAmenities, amenityTypes, userServiceRequests, serviceTypes, serviceRequestStatuses, userViolations, violationTypes, violationStatuses, apiOperationInProgress, lastApiOperation, isDataReady]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
            // Clean up Voiceflow on unmount
            cleanupVoiceflowChat();
        };
    }, [cleanupVoiceflowChat]);

    // Don't render if user is not authenticated
    if (!auth.isAuthenticated) {
        return null;
    }

    return (
        <div
            id={widgetId}
            style={{
                position: position,
                display: 'inline-block',
                ...customStyles
            }}
        >
            {/* Custom Chat Button */}
            {showCustomButton && isVoiceflowInitialized && (
                <button
                    onClick={toggleChat}
                    disabled={apiOperationInProgress}
                    style={
                        {
                            borderRadius: '50px',
                            padding: '10px',
                            fontWeight: '500',
                            cursor: apiOperationInProgress ? 'not-allowed' : 'pointer',
                            boxShadow: isChatOpen
                                ? '0 0 15px rgba(59, 130, 246, 0.5)'
                                : '0 2px 8px rgba(59, 130, 246, 0.3)',
                            opacity: apiOperationInProgress ? 0.7 : 1,
                            ...buttonStyle
                        }
                    }
                    className='bg-blue-400 text-[20px]'
                    onMouseEnter={(e) => {
                        if (!apiOperationInProgress) {
                            e.target.style.backgroundColor = '#2563EB';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!apiOperationInProgress) {
                            e.target.style.backgroundColor = isChatOpen ? '#2563EB' : '#3B82F6';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = isChatOpen
                                ? '0 0 15px rgba(59, 130, 246, 0.5)'
                                : '0 2px 8px rgba(59, 130, 246, 0.3)';
                        }
                    }}
                >
                    {apiOperationInProgress ? (
                        <>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid transparent',
                                borderTop: '2px solid white',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            Processing...
                        </>
                    ) : (
                        <>
                            {isChatOpen ? <TbMessageChatbotFilled className='scale-2' /> : <ButtonText className='scale-2' />}
                        </>
                    )}
                </button>
            )}

            {/* Loading state when chat is not ready */}
            {!isVoiceflowInitialized && isDataReady && (
                <div style={{
                    padding: '6px',
                    marginLeft: "10px",
                    backgroundColor: '#f3f4f6',
                    borderRadius: '50px',
                    color: '#6b7280',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid #6b7280',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    Loading Chat...
                </div>
            )}

            {/* Global styles */}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
    
                    /* Hide ALL default Voiceflow elements completely */
                    .vfrc-launcher,
                    .vfrc-widget-launcher,
                    .vfrc-widget--launcher,
                    .vfrc-assistant,
                    .vfrc-chat-launcher,
                    .vfrc-button,
                    .vfrc-fab,
                    [data-vf-chat-launcher],
                    [class*="vfrc-launcher"],
                    [class*="vfrc-button"],
                    [class*="vfrc-fab"] {
                        display: none !important;
                        visibility: hidden !important;
                        opacity: 0 !important;
                        pointer-events: none !important;
                        position: absolute !important;
                        left: -9999px !important;
                        top: -9999px !important;
                        width: 0 !important;
                        height: 0 !important;
                    }
    
                    /* Hide the chat widget when closed */
                    .vfrc-widget {
                        display: ${isChatOpen ? 'block' : 'none'} !important;
                        position: fixed !important;
                        bottom: 20px !important;
                        right: 20px !important;
                        z-index: 999999 !important;
                    }
    
                    /* Ensure our custom button shows properly */
                    #${widgetId} button {
                        position: relative;
                        z-index: 1000000;
                        display: flex !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                    }
    
                    /* Additional cleanup for any missed Voiceflow elements */
                    div[id*="voiceflow"],
                    div[class*="voiceflow"],
                    iframe[src*="voiceflow"] {
                        ${isChatOpen ? '' : 'display: none !important;'}
                    }
                `}
            </style>

            {/* Development debug info */}
            {process.env.NODE_ENV === 'development' && (
                <div style={{
                    position: 'absolute',
                    top: '40px',
                    left: '0',
                    background: '#333',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    whiteSpace: 'nowrap',
                    opacity: 0.8,
                    zIndex: 9999
                }}>
                    🚀 API: {isVoiceflowInitialized ? '✅' : '❌'} |
                    Data: {isDataReady ? '✅' : '⏳'} |
                    Op: {apiOperationInProgress ? '🔄' : '💤'} |
                    Chat: {isChatOpen ? '🔓' : '🔒'}
                </div>
            )}
        </div>
    );
};

export default ChatWidget;